import { isWaiting } from "../lib/schedule";
import { headlineValue, money, splitAddress, type JobCardFaceProps } from "./cardFaceTypes";
import styles from "./CardFaceCCompact.module.css";

export const VARIANT_C_NAME = "Compact — three lines";

/**
 * PROTOTYPE ONLY.
 *
 * Premise: the scarcest resource on a kanban board is vertical space. The
 * current face costs six lines, so roughly four cards fit in a column and the
 * board stops being a board. This one spends three, doubling what is visible,
 * and pays for it by deleting fields rather than shrinking them — references
 * live in the tooltip and in the open card, the client name only appears when
 * there is room left on the address line.
 *
 * The bet: everything cut here is a field people read *after* they have already
 * decided which card to open.
 */
export function VariantCCompact({ card }: JobCardFaceProps) {
  const { street, locality } = splitAddress(card.propertyAddress);
  const value = headlineValue(card);
  const waiting = isWaiting(card.scheduleAction ?? "");
  const refs = [card.refInternal && `#${card.refInternal}`, card.refExternal && `Ext ${card.refExternal}`]
    .filter(Boolean)
    .join("  ");

  return (
    <div
      className={styles.face}
      data-priority={card.priority ?? "normal"}
      title={[card.title, card.propertyAddress, card.clientName, refs].filter(Boolean).join("\n")}
    >
      <div className={styles.row}>
        <strong className={styles.title}>{card.title}</strong>
        {value && (
          <span className={value.estimated ? styles.valueEstimated : styles.value}>
            {money(value.cents)}
          </span>
        )}
      </div>

      <div className={styles.row}>
        <span className={styles.place}>
          {street}
          {locality && <span className={styles.locality}>, {locality}</span>}
        </span>
        {card.clientName && <span className={styles.client}>{card.clientName}</span>}
      </div>

      {waiting && card.scheduleLabel && (
        <span className={styles.waiting}>
          ⏳ {[card.scheduleLabel, card.scheduleWhen].filter(Boolean).join(" · ")}
        </span>
      )}
    </div>
  );
}
